import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Megaphone, Save, Loader2 } from "lucide-react";

const emptyBulletin = () => ({
  id: cryptoRandom(),
  text: "",
  link: "",
  active: true,
});

function cryptoRandom() {
  // Lightweight unique id for new rows (server replaces with uuid if needed)
  return `b_${Math.random().toString(36).slice(2, 10)}`;
}

export default function AdminAnnouncements() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("marquee");
  const [bulletins, setBulletins] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/announcements").then((r) => {
      if (!mounted) return;
      const d = r.data || {};
      setEnabled(!!d.enabled);
      setMode(d.mode || "marquee");
      setBulletins((d.bulletins || []).map((b) => ({ ...b, link: b.link || "" })));
    }).catch((e) => {
      toast.error("Failed to load announcements");
    }).finally(() => setLoading(false));
    return () => { mounted = false; };
  }, []);

  const addBulletin = () => {
    setBulletins((prev) => [...prev, emptyBulletin()]);
  };

  const updateBulletin = (id, patch) => {
    setBulletins((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const removeBulletin = (id) => {
    setBulletins((prev) => prev.filter((b) => b.id !== id));
  };

  const moveBulletin = (idx, dir) => {
    setBulletins((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const save = async () => {
    const cleaned = bulletins
      .map((b) => ({ ...b, text: (b.text || "").trim(), link: (b.link || "").trim() }))
      .filter((b) => b.text.length > 0);
    if (enabled && cleaned.length === 0) {
      toast.error("Add at least one bulletin with text before enabling the bar");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/announcements", {
        enabled,
        mode,
        bulletins: cleaned,
      });
      const saved = (r.data?.bulletins || []).map((b) => ({ ...b, link: b.link || "" }));
      setBulletins(saved);
      toast.success("Announcements saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-ink-soft text-sm uppercase tracking-widest">Loading…</div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="admin-announcements-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1 flex items-center gap-2"><Megaphone size={12} /> Operations</div>
          <h1 className="font-display text-4xl text-ink">Announcements</h1>
          <p className="text-ink-soft text-sm mt-2 max-w-xl">
            A site-wide bar pinned above the header. Use it for sales, dispatch delays, or any update you want customers to see immediately.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-ink hover:bg-ink/85 disabled:opacity-50 text-cream px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2 transition-colors"
          data-testid="announcement-save-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Global controls */}
      <div className="bg-white border border-border-subtle p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label className="text-ink font-semibold">Announcement bar enabled</Label>
              <p className="text-xs text-ink-soft mt-1">When off, the bar is hidden on every page (bulletins are preserved).</p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="announcement-enabled-switch"
            />
          </div>
          <div>
            <Label className="text-ink font-semibold mb-2 block">Display mode</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("marquee")}
                className={`px-3 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                  mode === "marquee" ? "border-ink bg-ink text-cream" : "border-border-subtle text-ink-soft hover:border-ink"
                }`}
                data-testid="announcement-mode-marquee"
              >
                Marquee (Scroll)
              </button>
              <button
                type="button"
                onClick={() => setMode("rotate")}
                className={`px-3 py-2.5 text-xs uppercase tracking-widest border transition-colors ${
                  mode === "rotate" ? "border-ink bg-ink text-cream" : "border-border-subtle text-ink-soft hover:border-ink"
                }`}
                data-testid="announcement-mode-rotate"
              >
                Rotate (Cycle)
              </button>
            </div>
            <p className="text-xs text-ink-soft mt-2">
              {mode === "marquee"
                ? "All bulletins are joined and scroll continuously right → left."
                : "Bulletins cycle one at a time, about 4.5 seconds each."}
            </p>
          </div>
        </div>
      </div>

      {/* Bulletins list */}
      <div className="bg-white border border-border-subtle">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h2 className="font-display text-xl text-ink">Bulletins</h2>
          <button
            onClick={addBulletin}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-sage hover:text-sage-dark border border-sage hover:border-sage-dark px-3 py-1.5 transition-colors"
            data-testid="announcement-add-btn"
          >
            <Plus size={12} /> Add bulletin
          </button>
        </div>
        {bulletins.length === 0 ? (
          <div className="px-5 py-12 text-center text-ink-soft text-sm">
            No bulletins yet. Add one to display an announcement on the site.
          </div>
        ) : (
          <ul>
            {bulletins.map((b, idx) => (
              <li
                key={b.id}
                className="flex items-start gap-3 p-4 border-b border-border-subtle last:border-0"
                data-testid={`announcement-row-${b.id}`}
              >
                <div className="flex flex-col items-center gap-1 pt-2 text-ink-soft">
                  <button
                    onClick={() => moveBulletin(idx, -1)}
                    disabled={idx === 0}
                    className="text-[10px] disabled:opacity-20 hover:text-ink"
                    title="Move up"
                    data-testid={`announcement-up-${b.id}`}
                  >▲</button>
                  <GripVertical size={12} />
                  <button
                    onClick={() => moveBulletin(idx, +1)}
                    disabled={idx === bulletins.length - 1}
                    className="text-[10px] disabled:opacity-20 hover:text-ink"
                    title="Move down"
                    data-testid={`announcement-down-${b.id}`}
                  >▼</button>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    value={b.text}
                    onChange={(e) => updateBulletin(b.id, { text: e.target.value })}
                    placeholder="e.g. End of Season Sale — Up to 40% off"
                    className="text-sm"
                    data-testid={`announcement-text-${b.id}`}
                    maxLength={240}
                  />
                  <Input
                    value={b.link}
                    onChange={(e) => updateBulletin(b.id, { link: e.target.value })}
                    placeholder="Optional link (e.g. /category/bedding)"
                    className="text-xs"
                    data-testid={`announcement-link-${b.id}`}
                  />
                </div>
                <div className="flex flex-col items-end gap-3 pt-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={b.active}
                      onCheckedChange={(v) => updateBulletin(b.id, { active: v })}
                      data-testid={`announcement-active-${b.id}`}
                    />
                    <span className="text-[10px] uppercase tracking-widest text-ink-soft">{b.active ? "Active" : "Hidden"}</span>
                  </div>
                  <button
                    onClick={() => removeBulletin(b.id)}
                    className="text-ink-soft hover:text-red-600 transition-colors"
                    title="Delete bulletin"
                    aria-label="Delete bulletin"
                    data-testid={`announcement-delete-${b.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Live preview */}
      <div className="mt-8" data-testid="announcement-preview-section">
        <div className="label-eyebrow mb-3">Preview</div>
        {enabled && bulletins.some((b) => b.active && b.text.trim()) ? (
          <PreviewBar mode={mode} bulletins={bulletins.filter((b) => b.active && b.text.trim())} />
        ) : (
          <div className="bg-cream-warm border border-border-subtle p-4 text-xs text-ink-soft text-center" data-testid="announcement-preview-empty">
            Bar is currently hidden on the site. Enable it and add at least one active bulletin to preview.
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewBar({ mode, bulletins }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (mode !== "rotate" || bulletins.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % bulletins.length), 4500);
    return () => clearInterval(t);
  }, [mode, bulletins.length]);

  if (mode === "marquee") {
    const joined = bulletins.map((b) => b.text).join("   ·   ");
    const seconds = Math.max(20, Math.min(80, joined.length * 0.35));
    return (
      <div className="bg-ink text-cream text-[13px] overflow-hidden announcement-marquee" data-testid="announcement-preview">
        <div className="py-2">
          <div className="announcement-track" style={{ animationDuration: `${seconds}s` }}>
            <span className="announcement-chunk">
              {bulletins.map((b, i) => (
                <React.Fragment key={`p1-${i}`}>
                  <span>{b.text}</span>
                  <span className="px-5 text-cream/60">·</span>
                </React.Fragment>
              ))}
            </span>
            <span className="announcement-chunk" aria-hidden="true">
              {bulletins.map((b, i) => (
                <React.Fragment key={`p2-${i}`}>
                  <span>{b.text}</span>
                  <span className="px-5 text-cream/60">·</span>
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      </div>
    );
  }
  const cur = bulletins[idx % bulletins.length];
  return (
    <div className="bg-ink text-cream text-[13px] text-center py-2" data-testid="announcement-preview">
      <span key={idx} className="announcement-rotate-item inline-block">{cur?.text}</span>
    </div>
  );
}
